import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import type { PlanQuotaMeta } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { discoveryQuotaQueryKey } from './discovery-quota-cache';

export { discoveryQuotaQueryKey, rememberDiscoveryQuota } from './discovery-quota-cache';

/**
 * The brand's Discovery search counter: what the last search of the session
 * stated, else what the features read (`GET /dashboard/features`, loaded with
 * the dashboard) states on arrival, so the counter shows before any search.
 * Undefined until either answers; a plan that counts nothing answers a null
 * `limit`.
 */
export function useDiscoveryQuota(brandId: string | undefined): PlanQuotaMeta | undefined {
  const { data, dataUpdatedAt: searchUpdatedAt } = useQuery<PlanQuotaMeta | null>({
    queryKey: discoveryQuotaQueryKey(brandId ?? ''),
    // Never fetched: the searches write it.
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const { data: features, dataUpdatedAt: featuresUpdatedAt } = usePlanFeaturesQuery();
  const onArrival = features?.discoverySearches;
  if (!data) return onArrival ?? undefined;
  if (!onArrival) return data;
  // Either response can arrive after a plan/month change, and a newer server
  // read can reduce usage when a pending search reservation expires. Choose
  // by response freshness rather than guessing from the allowance or count.
  return searchUpdatedAt > featuresUpdatedAt ? data : onArrival;
}
